/**
 * Sprint 103 — Pure helper for the tenant-library catalog pattern.
 *
 * Every library item kind (washPackages, services, addOns, packages,
 * vehicleTypes) carries an `excludedOutletIds: string[]` field. This
 * helper filters a library list down to the items that should be
 * visible at a given outlet.
 *
 * Pure: no I/O, no Firestore types. Used by:
 *   - customer-pwa `/buy-pack`, `/me/subscriptions` AvailablePacksSection
 *   - Cloud Functions when computing eligibility at the operator's POS
 *   - dashboard `/loyalty/packages` "show only items visible at this outlet"
 *     filter (UX nicety; the underlying CRUD is tenant-wide)
 *
 * Why "exclude-list" not "include-list":
 *   - Default `[]` = available everywhere → matches the smart-default UX
 *     (operator-defined items default to ALL outlets).
 *   - Adding a new outlet automatically includes every existing library
 *     item — the right behaviour 90% of the time without re-assignment.
 *   - Include-list would invert this and create a friction chore every
 *     time an outlet is added.
 *
 * See [[project-tenant-library-catalog]].
 */

/**
 * Minimal shape every library item satisfies. Each item's full schema
 * is its own type (`WashPackage`, future `Service`, etc.) — this
 * helper's input type is the structural subset it actually reads.
 */
export interface ExcludableItem {
  readonly excludedOutletIds?: readonly string[];
}

/**
 * Filter a tenant-library item list down to the items visible at the
 * given outlet. An item with no `excludedOutletIds` field (or an empty
 * array) is visible at every outlet — that's the default-everywhere
 * behaviour.
 *
 * Stable order: preserves the input array's order (callers handle their
 * own sort — typically by `rank` field).
 */
export function filterForOutlet<T extends ExcludableItem>(
  items: readonly T[],
  outletId: string,
): T[] {
  return items.filter((i) => !(i.excludedOutletIds ?? []).includes(outletId));
}

/**
 * Inverse — items HIDDEN at this outlet. Useful for the operator CRUD
 * page's "Hidden here" badge and for super-admin debug surfaces.
 */
export function hiddenAtOutlet<T extends ExcludableItem>(
  items: readonly T[],
  outletId: string,
): T[] {
  return items.filter((i) => (i.excludedOutletIds ?? []).includes(outletId));
}

/**
 * For the CRUD page's per-outlet checkbox UX: given an item + the full
 * outlet list, return a stable map of `outletId → checked` where
 * `checked === true` means "visible at this outlet" (i.e., NOT in the
 * exclude list). The form binds against this state; on save the
 * unchecked outlet ids become the new `excludedOutletIds` array.
 */
export function outletAssignmentMap(
  excludedOutletIds: readonly string[] | undefined,
  allOutletIds: readonly string[],
): Record<string, boolean> {
  const excludedSet = new Set(excludedOutletIds ?? []);
  const out: Record<string, boolean> = {};
  for (const oid of allOutletIds) {
    out[oid] = !excludedSet.has(oid);
  }
  return out;
}

/**
 * Inverse of `outletAssignmentMap` — fold the form state back into the
 * `excludedOutletIds` array. Idempotent + stable order (matches
 * `allOutletIds` input).
 */
export function excludedOutletIdsFromMap(
  assignmentMap: Readonly<Record<string, boolean>>,
  allOutletIds: readonly string[],
): string[] {
  return allOutletIds.filter((oid) => assignmentMap[oid] === false);
}
